import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Container,
  Fab,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Add,
  CloudDownload,
  CloudUpload,
  HelpOutline,
  MoreVert,
} from "@material-ui/icons";
import { FormattedMessage, useIntl } from "react-intl";

import Title from "../../ui/Title";
import FloatingActions from "../../ui/FloatingActions";
import Confirm from "../../ui/Confirm";

import { confirm as confirmMsgs, account, common } from "../../messages";
import { useBmapi } from "../../utils/bmapi-context";
import { getErrorMessageString } from "../../utils/errors";
import { langs } from "../../utils/dictionaries";
import { courses as coursesMsgs } from "../../messages/courses";
import GenListDropdown from "../../components/genlist/GenListDropdown";
import FreeTextChipsInput from "../../components/FreeTextChipsInput";
import MultipleSelectionsWithChip from "../../components/MultipleSelectionWithChip";
import CampusCourseEntryMode from "../../components/CampusCourseEntryMode";

const useStyles = makeStyles(() => ({
  primaryClamp2: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  chipRow: {
    marginTop: 4,
    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      marginRight: 6,
      marginBottom: 6,
    },
  },
  excelActions: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: 16,
    "& > *": {
      marginRight: 12,
      marginBottom: 8,
    },
  },
  courseCard: {
    marginBottom: 12,
  },
  courseHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  courseTitle: {
    paddingRight: 12,
  },
  courseMeta: {
    marginTop: 4,
  },
  courseDescription: {
    marginTop: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  courseBottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 12,
    minWidth: 0,
  },
  locationsGroup: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    overflow: "hidden",
    flex: 1,
  },
  locationsLabel: {
    marginRight: 8,
    flexShrink: 0,
  },
  locationsChips: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  languagesGroup: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexShrink: 0,
    marginLeft: 16,
  },
  languageFlag: {
    width: 24,
    marginLeft: 8,
    display: "block",
  },
  helpField: {
    padding: "10px 0",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  helpValues: {
    marginTop: 8,
    padding: 10,
    maxHeight: 180,
    overflowY: "auto",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 4,
    background: "rgba(0, 0, 0, 0.02)",
  },
  fabWrap: { position: "relative", display: "inline-flex" },
  fabProgress: {
    position: "absolute",
    top: -6,
    right: -6,
    zIndex: 1,
  },
}));

const LANGUAGE_OPTIONS = [
  { value: "it", label: "Italiano" },
  { value: "en", label: "Inglese" },
  { value: "fr", label: "Francese" },
  { value: "es", label: "Spagnolo" },
  { value: "de", label: "Tedesco" },
];

const ACCESS_OPTIONS = [
  { value: "onsite", label: "In presenza" },
  { value: "online", label: "Telematica" },
];

const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const getGenListLabels = (genList, lang = "it") => {
  const items = Array.isArray(genList?.items) ? genList.items : [];
  const allLangs = Array.isArray(genList?.all_lang) ? genList.all_lang : [];
  const langIndex = allLangs.indexOf(lang);

  const labels = items
    .map((item) => {
      if (
        Array.isArray(item?.values) &&
        langIndex >= 0 &&
        item.values[langIndex]?.str_value !== undefined
      ) {
        return String(item.values[langIndex].str_value || "").trim();
      }

      return "";
    })
    .filter(Boolean);

  return [...new Set(labels)].sort((a, b) =>
    a.localeCompare(b, "it", { sensitivity: "base" })
  );
};

const getGeoAreaHelpValues = (genList, lang = "it") => {
  const items = Array.isArray(genList?.items) ? genList.items : [];
  const allLangs = Array.isArray(genList?.all_lang) ? genList.all_lang : [];
  const langIndex = allLangs.indexOf(lang);

  const getLabel = (item) => {
    if (
      Array.isArray(item?.values) &&
      langIndex >= 0 &&
      item.values[langIndex]?.str_value !== undefined
    ) {
      return String(item.values[langIndex].str_value || "").trim();
    }

    return "";
  };

  const sortLabels = (values) =>
    [...new Set(values.filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, "it", { sensitivity: "base" })
    );

  return {
    regions: sortLabels(
      items.filter((item) => !item?.type || item.type === "R").map(getLabel)
    ),
    countries: sortLabels(
      items.filter((item) => item?.type === "N").map(getLabel)
    ),
  };
};

const normalizeExcelFileName = (value) => {
  const normalized = (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

  return normalized || "business";
};

export default function ManageCampusCourses() {
  const classes = useStyles();
  const intl = useIntl();
  const { bmapi, notifyError, notifySuccess, businessId } = useBmapi();

  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const excelFileInputRef = useRef(null);
  const [excelBusy, setExcelBusy] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpListsBusy, setHelpListsBusy] = useState(false);
  const [helpListsLoaded, setHelpListsLoaded] = useState(false);
  const [helpAreaValues, setHelpAreaValues] = useState([]);
  const [helpGeoRegions, setHelpGeoRegions] = useState([]);
  const [helpGeoCountries, setHelpGeoCountries] = useState([]);

  // conferme
  const [deleteRequest, setDeleteRequest] = useState(false);
  const [closeEditorConfirm, setCloseEditorConfirm] = useState(false);

  // anno (int) 2024 -> current+3
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const yearOptions = useMemo(() => {
    const max = currentYear + 3;
    const out = [];
    for (let y = 2024; y <= max; y++) out.push(y);
    return out;
  }, [currentYear]);

  const helpFields = useMemo(
    () => [
      {
        name: "Codice",
        rules:
          "Obbligatorio. Massimo 10 caratteri. Deve essere univoco per il business. Nel file Excel viene usato per riconoscere il corso.",
      },
      {
        name: "Codice MIUR",
        rules: "Opzionale. Testo libero.",
      },
      {
        name: "Anno",
        rules: `Obbligatorio. Valori ammessi da 2024 a ${currentYear + 3}.`,
      },
      {
        name: "Nome",
        rules: "Obbligatorio. Non può essere vuoto.",
      },
      {
        name: "Comuni/Località",
        rules:
          "Opzionale. Massimo 20 valori, massimo 50 caratteri per valore. Nel file Excel separare più valori con la barra verticale (pipe) |.",
      },
      {
        name: "Regione/Area geografica",
        rules:
          "Opzionale. Massimo 10 valori. Nel file Excel separare più valori con la barra verticale (pipe) |.",
        groups: [
          { name: "Regioni", values: helpGeoRegions },
          { name: "Nazioni", values: helpGeoCountries },
        ],
      },
      {
        name: "Lingue",
        rules:
          "Valori ammessi: Italiano, Inglese, Francese, Tedesco. Nel file Excel separare più valori con la barra verticale (pipe) |.",
      },
      {
        name: "Modalità di accesso",
        rules:
          "Valori ammessi: vuoto, Nazionale, Locale, Accesso libero, Portfolio e Test d'ingresso, Libero con test di orientamento.",
      },
      {
        name: "Modalità di erogazione",
        rules:
          "Valori ammessi: In presenza, Telematica. Nel file Excel separare più valori con la barra verticale (pipe) |.",
      },
      {
        name: "URL",
        rules:
          "Opzionale. Se valorizzato deve iniziare con http:// oppure https://.",
      },
      {
        name: "Descrizione",
        rules: "Opzionale. Testo libero.",
      },
      {
        name: "Ambito",
        rules:
          "Nel file Excel separare più valori con la barra verticale (pipe) |.",
        values: helpAreaValues,
      },
    ],
    [currentYear, helpAreaValues, helpGeoRegions, helpGeoCountries]
  );

  useEffect(() => {
    if (!helpOpen || !bmapi || helpListsLoaded) {
      return;
    }

    let isMounted = true;

    const loadHelpLists = async () => {
      setHelpListsBusy(true);

      try {
        const [areaList, geoAreaList] = await Promise.all([
          bmapi.getGenListAdmin("area"),
          bmapi.getGenListAdmin("business_locations"),
        ]);

        if (!isMounted) return;

        const geoValues = getGeoAreaHelpValues(geoAreaList, "it");

        setHelpAreaValues(getGenListLabels(areaList, "it"));
        setHelpGeoRegions(geoValues.regions);
        setHelpGeoCountries(geoValues.countries);
        setHelpListsLoaded(true);
      } catch (e) {
        if (!isMounted) return;

        notifyError(getErrorMessageString(e, intl));
      } finally {
        if (isMounted) {
          setHelpListsBusy(false);
        }
      }
    };

    loadHelpLists();

    return () => {
      isMounted = false;
    };
  }, [bmapi, helpOpen, helpListsLoaded, intl, notifyError]);

  // editor popup
  const [editor, setEditor] = useState({
    open: false,
    mode: "create", // 'create' | 'edit'
    id: null,
    values: {
      code: "",
      year: currentYear, // int
      name: "",
      description: "",
      url: "",
      area: [],
      geo_area: [],
      locations: [],
      course_langs: ["it"], // default
      entry_mode: "", // string
      access: [], // <-- nuovo campo (array di stringhe)
    },
    errors: {
      code: false,
      year: false,
      name: false,
      description: false,
      url: false,
      area: false,
      geo_area: false,
      locations: false,
      course_langs: false,
      entry_mode: false,
      access: false,
    },
    dirty: false,
    busy: false,
  });

  const loadCoursesList = useCallback(() => {
    if (!bmapi) return;
    bmapi
      .getCourses({
        business: businessId,
        lang: bmapi.getUserInfo().business.default_language,
      })
      .then((resp) =>
        setList(
          (resp || []).slice().sort((a, b) =>
            (a?.name ?? "").localeCompare(b?.name ?? "", undefined, {
              sensitivity: "base",
            })
          )
        )
      )
      .catch((e) => notifyError(getErrorMessageString(e, intl)));
  }, [bmapi, intl, notifyError, businessId]);

  useEffect(() => {
    loadCoursesList();
  }, [loadCoursesList]);

  const handleExportCoursesExcel = async () => {
    if (!bmapi || excelBusy) return;

    setExcelBusy(true);

    try {
      const response = await bmapi.campusExportCoursesExcel({
        params: {
          business: businessId,
        },
      });

      let blob;

      if (response instanceof Blob) {
        blob = response;
      } else if (
        typeof Response !== "undefined" &&
        response instanceof Response
      ) {
        blob = await response.blob();
      } else {
        blob = new Blob([response], {
          type: XLSX_MIME_TYPE,
        });
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const businessName = bmapi.getUserInfo()?.business?.name;
      const fileName = `${normalizeExcelFileName(businessName)}_corsi.xlsx`;

      link.href = downloadUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      notifyError(getErrorMessageString(e, intl));
    } finally {
      setExcelBusy(false);
    }
  };

  const handleSelectCoursesExcel = () => {
    if (excelBusy) return;
    excelFileInputRef.current?.click();
  };

  const handleImportCoursesExcel = async (event) => {
    const file = event.target.files?.[0];

    // Permette di selezionare nuovamente lo stesso file.
    event.target.value = "";

    if (!file || !bmapi || excelBusy) return;

    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      notifyError("Seleziona un file Excel in formato .xlsx");
      return;
    }

    setExcelBusy(true);

    try {
      const result = await bmapi.campusImportCoursesExcel(file, {
        business: businessId,
      });

      const created = result?.created ?? 0;
      const updated = result?.updated ?? 0;
      const unchanged = result?.unchanged ?? 0;

      notifySuccess(
        `Import completato: ${created} creati, ${updated} modificati, ${unchanged} invariati`
      );

      loadCoursesList();
    } catch (e) {
      notifyError(getErrorMessageString(e, intl));
    } finally {
      setExcelBusy(false);
    }
  };

  // MENU
  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
  };
  const handleMenuClose = () => setAnchorEl(null);

  // EDITOR
  const openCreateEditor = () =>
    setEditor({
      open: true,
      mode: "create",
      id: null,
      values: {
        code: "",
        year: currentYear,
        name: "",
        description: "",
        url: "",
        area: [],
        geo_area: [],
        locations: [],
        course_langs: ["it"],
        entry_mode: "",
        access: [],
      },
      errors: {
        code: false,
        year: false,
        name: false,
        description: false,
        url: false,
        area: false,
        geo_area: false,
        locations: false,
        course_langs: false,
        entry_mode: false,
        access: false,
      },
      dirty: false,
      busy: false,
    });

  const openEditEditor = (id) => {
    const item = list.find((x) => x.id === id);
    setEditor({
      open: true,
      mode: "edit",
      id,
      values: {
        code: item?.code ?? "",
        year: Number(item?.year) || currentYear,
        name: item?.name ?? "",
        description: item?.description ?? "",
        url: item?.url ?? "",
        area: Array.isArray(item?.area) ? item.area : [],
        geo_area: Array.isArray(item?.geo_area) ? item.geo_area : [],
        locations: Array.isArray(item?.locations) ? item.locations : [],
        course_langs:
          Array.isArray(item?.course_langs) && item.course_langs.length
            ? item.course_langs
            : ["it"],
        entry_mode: item?.entry_mode ?? "",
        access: Array.isArray(item?.access) ? item.access : [], // <-- carica access
      },
      errors: {
        code: false,
        year: false,
        name: false,
        description: false,
        url: false,
        area: false,
        geo_area: false,
        locations: false,
        course_langs: false,
        entry_mode: false,
        access: false,
      },
      dirty: false,
      busy: false,
    });
  };

  const requestCloseEditor = () => {
    if (editor.busy) return;
    if (editor.dirty) {
      setCloseEditorConfirm(true);
      return;
    }
    setEditor((s) => ({ ...s, open: false }));
  };

  const confirmCloseEditor = () => {
    setCloseEditorConfirm(false);
    setEditor((s) => ({ ...s, open: false, dirty: false }));
  };

  const handleField = (field) => (e) => {
    const val = e.target.value;
    setEditor((s) => ({
      ...s,
      values: { ...s.values, [field]: val },
      dirty: true,
      errors: { ...s.errors, [field]: false },
    }));
  };

  // year come number
  const handleYear = (e) => {
    const val = Number(e.target.value);
    setEditor((s) => ({
      ...s,
      values: { ...s.values, year: val },
      dirty: true,
      errors: { ...s.errors, year: false },
    }));
  };

  const validate = () => {
    const code = editor.values.code?.trim() || "";
    const codeInvalid = code.length === 0 || code.length > 10;
    const nameInvalid = !editor.values.name?.trim();
    const urlStr = editor.values.url?.trim() || "";
    const urlInvalid = urlStr.length > 0 && !/^https?:\/\//i.test(urlStr); // semplice check

    const errs = {
      code: codeInvalid,
      year: false,
      name: nameInvalid,
      description: false,
      url: urlInvalid,
      area: false,
      geo_area: false,
      locations: false,
      course_langs: false,
      entry_mode: false,
      access: false,
    };
    setEditor((s) => ({ ...s, errors: errs }));
    return !codeInvalid && !nameInvalid && !urlInvalid;
  };

  const saveEditor = async () => {
    if (!bmapi) return;
    if (!validate()) return;

    setEditor((s) => ({ ...s, busy: true }));
    try {
      const payload = {
        code: editor.values.code.trim(),
        year: Number(editor.values.year) || currentYear, // int
        name: editor.values.name.trim(),
        description: editor.values.description?.trim() ?? "",
        url: editor.values.url?.trim() ?? "",
        area: Array.isArray(editor.values.area) ? editor.values.area : [],
        geo_area: Array.isArray(editor.values.geo_area)
          ? editor.values.geo_area
          : [],
        locations: Array.isArray(editor.values.locations)
          ? editor.values.locations
          : [],
        course_langs: Array.isArray(editor.values.course_langs)
          ? editor.values.course_langs
          : ["it"],
        entry_mode: editor.values.entry_mode ?? "",
        access: Array.isArray(editor.values.access) ? editor.values.access : [], // <-- invia access
      };

      if (editor.mode === "edit" && editor.id) {
        await bmapi.updateCourse(editor.id, payload);
      } else {
        await bmapi.createCourse(payload);
      }

      notifySuccess(intl.formatMessage(account.saveConfirm));
      setEditor((s) => ({ ...s, busy: false, open: false, dirty: false }));
      loadCoursesList();
    } catch (e) {
      setEditor((s) => ({ ...s, busy: false }));
      notifyError(getErrorMessageString(e, intl));
    }
  };

  // DELETE
  const handleDeleteClick = () => {
    setDeleteRequest(true);
    handleMenuClose();
  };
  const onDeleteCourse = async () => {
    if (!selectedId) return;
    try {
      await bmapi.deleteCourse(selectedId);
      notifySuccess(intl.formatMessage(common.deleteElement));
      setDeleteRequest(false);
      setSelectedId(null);
      loadCoursesList();
    } catch (e) {
      notifyError(getErrorMessageString(e, intl));
    }
  };

  return (
    <Container maxWidth="md">
      <Title>
        <FormattedMessage
          id="navigation.manager.courses"
          defaultMessage="Corsi"
        />
      </Title>

      <input
        ref={excelFileInputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: "none" }}
        onChange={handleImportCoursesExcel}
      />

      <Box className={classes.excelActions}>
        <Button
          variant="outlined"
          startIcon={<CloudDownload />}
          onClick={handleExportCoursesExcel}
          disabled={editor.busy || excelBusy}
        >
          Esporta (Excel)
        </Button>

        <Button
          variant="outlined"
          startIcon={<CloudUpload />}
          onClick={handleSelectCoursesExcel}
          disabled={editor.busy || excelBusy}
        >
          Importa (Excel)
        </Button>

        <Button
          variant="outlined"
          startIcon={<HelpOutline />}
          onClick={() => setHelpOpen(true)}
          disabled={editor.busy || excelBusy}
        >
          Aiuto
        </Button>

        {excelBusy && <CircularProgress size={24} />}
      </Box>

      <Box>
        {list.map((course) => {
          const courseLanguages = Array.isArray(course.course_langs)
            ? course.course_langs
            : [];
          const courseLocations = Array.isArray(course.locations)
            ? course.locations
            : [];

          return (
            <Card
              key={course.id}
              className={classes.courseCard}
              variant="outlined"
            >
              <CardContent>
                <Box className={classes.courseHeader}>
                  <Box className={classes.courseTitle}>
                    <Typography variant="h6">{course.name}</Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={classes.courseMeta}
                    >
                      Codice: {course.code || "—"} · Anno: {course.year || "—"}
                    </Typography>
                  </Box>

                  <IconButton
                    aria-label="menu"
                    onClick={(e) => handleMenuOpen(e, course.id)}
                  >
                    <MoreVert />
                  </IconButton>
                </Box>

                {course.description && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.courseDescription}
                    title={course.description}
                  >
                    {course.description}
                  </Typography>
                )}

                {(courseLocations.length > 0 || courseLanguages.length > 0) && (
                  <Box className={classes.courseBottomRow}>
                    <Box className={classes.locationsGroup}>
                      {courseLocations.length > 0 && (
                        <>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            className={classes.locationsLabel}
                          >
                            Località
                          </Typography>

                          <Box className={classes.locationsChips}>
                            {courseLocations.map((loc, i) => (
                              <Chip
                                key={`${loc}-${i}`}
                                label={loc}
                                size="small"
                                style={{ marginRight: 6 }}
                              />
                            ))}
                          </Box>
                        </>
                      )}
                    </Box>

                    {courseLanguages.length > 0 && (
                      <Box className={classes.languagesGroup}>
                        {courseLanguages.map((langCode) => {
                          const lang = langs.find((l) => l.value === langCode);

                          if (!lang || !lang.Flag) {
                            return (
                              <Chip
                                key={langCode}
                                size="small"
                                label={langCode}
                                style={{ marginLeft: 6 }}
                              />
                            );
                          }

                          const Flag = lang.Flag;

                          return (
                            <Tooltip key={langCode} title={lang.label}>
                              <span>
                                <Flag className={classes.languageFlag} />
                              </span>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            openEditEditor(selectedId);
            handleMenuClose();
          }}
        >
          Modifica
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>Elimina</MenuItem>
      </Menu>

      {/* Guida campi */}
      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Guida compilazione file Excel</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary">
            Nel file Excel, per i campi che accettano più valori, separare i
            valori con la barra verticale (pipe) |.
          </Typography>

          <Box mt={2}>
            {helpFields.map((field) => (
              <Box key={field.name} className={classes.helpField}>
                <Typography variant="subtitle2">{field.name}</Typography>
                <Typography variant="body2">{field.rules}</Typography>

                {field.values && (
                  <Box className={classes.helpValues}>
                    {helpListsBusy && field.values.length === 0 ? (
                      <Box display="flex" alignItems="center">
                        <CircularProgress size={18} />
                        <Box ml={1}>
                          <Typography variant="body2">
                            Caricamento valori...
                          </Typography>
                        </Box>
                      </Box>
                    ) : field.values.length > 0 ? (
                      <Typography variant="body2">
                        {field.values.join(", ")}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Nessun valore disponibile.
                      </Typography>
                    )}
                  </Box>
                )}

                {field.groups && (
                  <Box className={classes.helpValues}>
                    {helpListsBusy &&
                    field.groups.every((group) => group.values.length === 0) ? (
                      <Box display="flex" alignItems="center">
                        <CircularProgress size={18} />
                        <Box ml={1}>
                          <Typography variant="body2">
                            Caricamento valori...
                          </Typography>
                        </Box>
                      </Box>
                    ) : field.groups.some(
                        (group) => group.values.length > 0
                      ) ? (
                      field.groups
                        .filter((group) => group.values.length > 0)
                        .map((group, index) => (
                          <Box key={group.name} mt={index === 0 ? 0 : 2}>
                            <Typography variant="subtitle2">
                              {group.name}:
                            </Typography>

                            <Typography variant="body2">
                              {group.values.join(", ")}
                            </Typography>
                          </Box>
                        ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Nessun valore disponibile.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setHelpOpen(false)}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Editor popup */}
      <Dialog
        open={editor.open}
        onClose={requestCloseEditor}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editor.mode === "edit"
            ? intl.formatMessage(
                coursesMsgs.editCourse || {
                  id: "courses.edit",
                  defaultMessage: "Modifica corso",
                }
              )
            : intl.formatMessage(
                coursesMsgs.addCourse || {
                  id: "courses.add",
                  defaultMessage: "Nuovo corso",
                }
              )}
        </DialogTitle>
        <DialogContent>
          {/* CODICE */}
          <TextField
            autoFocus
            margin="dense"
            label="Codice"
            value={editor.values.code}
            onChange={handleField("code")}
            fullWidth
            variant="standard"
            error={editor.errors.code}
            helperText={
              editor.errors.code
                ? "Obbligatorio, massimo 10 caratteri"
                : editor.mode === "edit"
                ? "Il codice non è modificabile"
                : ""
            }
            inputProps={{ maxLength: 10 }}
            disabled={editor.busy || editor.mode === "edit"}
          />

          {/* ANNO (int) */}
          <TextField
            select
            margin="dense"
            label="Anno"
            value={editor.values.year ?? currentYear}
            onChange={handleYear}
            fullWidth
            variant="standard"
          >
            {yearOptions.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </TextField>

          {/* NOME */}
          <TextField
            margin="dense"
            label="Nome"
            value={editor.values.name}
            onChange={handleField("name")}
            fullWidth
            variant="standard"
            error={editor.errors.name}
            helperText={editor.errors.name ? "Obbligatorio" : ""}
            disabled={editor.busy}
          />

          {/* LOCATIONS */}
          <FreeTextChipsInput
            label="Comuni/Località"
            placeholder="Aggiungi e premi Invio"
            value={
              Array.isArray(editor.values.locations)
                ? editor.values.locations
                : []
            }
            onChange={(next) =>
              handleField("locations")({ target: { value: next } })
            }
            disabled={editor.busy}
            margin="dense"
            maxItems={20}
            maxLength={50}
          />

          {/* AREA GEOGRAFICA */}
          <GenListDropdown
            onChange={handleField("geo_area")}
            margin="dense"
            label="Regione/Area geografica"
            listId="business_locations"
            value={
              Array.isArray(editor.values.geo_area)
                ? editor.values.geo_area
                : []
            }
            name="geo_area"
            multiple={true}
            lang="it"
          />

          {/* LINGUE (course_langs) con chip cancellabili */}
          <MultipleSelectionsWithChip
            label="Lingue"
            name="course_langs"
            options={LANGUAGE_OPTIONS}
            value={
              Array.isArray(editor.values.course_langs)
                ? editor.values.course_langs
                : []
            }
            onChange={handleField("course_langs")}
            margin="dense"
          />

          {/* ACCESSO (entry_mode) — subito dopo le lingue */}
          <CampusCourseEntryMode
            value={editor.values.entry_mode}
            onChange={handleField("entry_mode")}
          />

          {/* MODALITÀ DI EROGAZIONE (access) */}
          <MultipleSelectionsWithChip
            label="Modalità"
            name="access"
            options={ACCESS_OPTIONS}
            value={
              Array.isArray(editor.values.access) ? editor.values.access : []
            }
            onChange={handleField("access")}
            margin="dense"
          />

          {/* URL */}
          <TextField
            margin="dense"
            label="URL"
            value={editor.values.url}
            onChange={handleField("url")}
            fullWidth
            variant="standard"
            error={editor.errors.url}
            helperText={
              editor.errors.url ? "URL non valido: usa http:// o https://" : ""
            }
            disabled={editor.busy}
          />

          {/* DESCRIZIONE */}
          <TextField
            margin="dense"
            label="Descrizione"
            value={editor.values.description}
            onChange={handleField("description")}
            fullWidth
            variant="standard"
            multiline
            rows={3}
            error={editor.errors.description}
            disabled={editor.busy}
          />

          {/* AMBITO */}
          <GenListDropdown
            onChange={handleField("area")}
            margin="dense"
            label="Ambito"
            listId="area"
            value={Array.isArray(editor.values.area) ? editor.values.area : []}
            name="area"
            multiple={true}
            lang="it"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={requestCloseEditor} disabled={editor.busy}>
            Chiudi
          </Button>
          <Box position="relative" display="inline-flex">
            <Button color="primary" onClick={saveEditor} disabled={editor.busy}>
              Salva
            </Button>
            {editor.busy && (
              <CircularProgress
                size={32}
                style={{ position: "absolute", top: -6, right: -10, zIndex: 1 }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Conferma chiusura editor con modifiche non salvate */}
      <Confirm
        open={!!closeEditorConfirm}
        onConfirm={confirmCloseEditor}
        onCancel={() => setCloseEditorConfirm(false)}
        text={
          intl.formatMessage?.(
            confirmMsgs?.exitPrompt || {
              id: "confirm.exitPrompt",
              defaultMessage: "Hai modifiche non salvate. Uscire comunque?",
            }
          ) || "Hai modifiche non salvate. Uscire comunque?"
        }
      />

      {/* Conferma eliminazione */}
      <Confirm
        open={!!deleteRequest}
        onConfirm={onDeleteCourse}
        onCancel={() => setDeleteRequest(false)}
        text={intl.formatMessage(confirmMsgs.deleteElement)}
      />

      <FloatingActions>
        <span className={classes.fabWrap}>
          <Fab
            variant="extended"
            color="primary"
            onClick={openCreateEditor}
            disabled={editor.busy || excelBusy}
          >
            <Add />
            {intl.formatMessage(
              coursesMsgs.addCourse || {
                id: "courses.add",
                defaultMessage: "Nuovo corso",
              }
            )}
          </Fab>

          {(editor.busy || excelBusy) && (
            <CircularProgress size={48} className={classes.fabProgress} />
          )}
        </span>
      </FloatingActions>
    </Container>
  );
}
